import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroupDirective, NgForm } from '@angular/forms';
import { Router } from '@angular/router';
import { AppDataService, AuthService, GooglePlacesService, PlaceAutocompleteSuggestionDto, XpressUserService } from 'app/services';
import { AppComponent, RootEnum } from 'app/app.component';
import { BusinessDocumentsEnum, SignupParams, SignupStepEnum } from './signup-params';
import { XpPasswordStrengthLabels, XpPasswordStrengthLevel, xpPasswordStrength } from '@xp/password-strength/xp-password-strength.component';
import { OperatorModel } from 'app/operator/operator-signup/OperatorModel';
import {
    MenuPermissionActionEnum,
    MenuPermissionConfig,
    XpressUserRoleEnum
} from 'app/models';
import { normalizeUploadedUrls, Utils } from 'app/utils';
import { MessageService } from 'primeng/api';
import { OperatorService } from 'app/services/operator.service';
import { ErrorStateMatcher } from '@angular/material/core';
import { Observable, Subject } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';
import { OperatorDashboardTourService } from 'app/components/operator/dashboard/operator-dashboard-tour.service';

@Component({
    selector: 'operator-signup',
    templateUrl: './operator-signup.component.html',
    styleUrls: ['./operator-signup.component.scss']
})
export class OperatorSignupComponent implements OnInit, OnDestroy{

    emailErrorMsg: string | null = null;
    termsErrorVisible = false;
    taxErrorVisible = false;
    accErrorMsg: string | null = null;
    isLoadingOverlay = false;

    buildNumber: string | null = '';
    apiBaseUrl: string | null  = '';
    apiVersion: string | null  = '';
    apiUrl: string | null = '';

    passwordTouched = false;
    confirmPasswordTouched = false;

    readonly passwordScoreLabels: XpPasswordStrengthLabels = {
        empty: SignupParams.Empty,
        weak: SignupParams.Weak,
        medium: SignupParams.Medium,
        strong: SignupParams.Strong
    };

    readonly stepPasswordRules = [
        'At least one lowercase letter',
        'At least one uppercase letter',
        'At least one numeric character',
        'Minimum 8 characters'
    ];

    readonly resetPasswordRules = [
        'At least one lowercase',
        'At least one uppercase',
        'At least one numeric',
        'Minimum 8 characters'
    ];

    showPasswordTips: boolean = false;
    accountCreationInProgress: boolean = false;
    verifyingEmailInProgress: boolean = false;
    showLeaveWithoutSavingDialog = false;

    currentStep: SignupStepEnum = SignupStepEnum.OperatorDetails;

    step1class = SignupParams.CurrentStep;
    step2class = SignupParams.NextStep;
    step3class = SignupParams.NextStep;
    step4class = SignupParams.NextStep;

    btnContinueText = SignupParams.ContinueBtn;
    title = SignupParams.FirstTitle;
    subtitle = SignupParams.FirstSubtitle;

    loading: boolean = false;
    emailAlreadyExist: boolean = false;
    emailAvailabilityChecked: boolean = false;
    emailErrorMatcher: ErrorStateMatcher = {
        isErrorState: (control: FormControl | null, form: FormGroupDirective | NgForm | null) =>
            !!(control && (control.invalid || this.emailAlreadyExist) && (control.touched || form?.submitted))
    };
    operatorResetPassword: boolean = false;
    merchantResetPassword: boolean = false;
    tourOperatorResetPassword: boolean = false;
    processId: string = '';
    passChangedSuccess: boolean = false;

    govIdProgressBar: number = 0;
    private govIdPreviewUrls = new Map<File, string>();
    DTIorSECProgressBar: number = 0;
    BIRProgressBar: number = 0;
    LTFRBProgressBar: number = 0;
    BusinessPermitProgressBar: number = 0;

    formMarginTop: number = SignupParams.MarginTopPrimary;


    govIdInterval: any;
    DTIorSECInterval: any;
    BIRInterval: any;
    LTFRBInterval: any;
    BusinessPermitInterval: any;

    govIdUploadError: boolean = false;
    DTIorSECUploadError: boolean = false;
    BIRUploadError: boolean = false;
    LTFRBUploadError: boolean = false;
    BusinessPermitUploadError: boolean = false;

    operator: OperatorModel = new OperatorModel();
    isPasswordEqual: boolean = true;

    signUpProcessId!: string;
    isOtpRequest: boolean = false;
    isOtpVerified: boolean = false;
    addressSuggestions: PlaceAutocompleteSuggestionDto[] = [];
    addressSearchError = '';
    private selectedAddress = '';
    private readonly destroy$ = new Subject<void>();
    private readonly leaveSignupConfirmed$ = new Subject<boolean>();
    private allowLeaveSignup = false;
    private pendingLeaveAction: (() => void) | null = null;
    private offerDashboardTourOnFirstLogin = false;

    constructor(
        private operatorService: OperatorService,
        private xpressUserService: XpressUserService,
        private authService: AuthService,
        private messageService: MessageService,
        public appData: AppDataService,
        public app: AppComponent,
        private router: Router,
        private googlePlacesService: GooglePlacesService
        ) {}

    ngOnInit(): void {
        this.buildNumber = sessionStorage.getItem('BuildNumber');
        this.apiBaseUrl = sessionStorage.getItem('API_BaseUrl');
        this.apiVersion = sessionStorage.getItem('API_Version');
        this.operatorResetPassword = sessionStorage.getItem('operatorResetPassword') as unknown as boolean;
        this.merchantResetPassword = sessionStorage.getItem('merchantResetPassword') as unknown as boolean;
        this.tourOperatorResetPassword = sessionStorage.getItem('tourOperatorResetPassword') as unknown as boolean;

        if (this.operatorResetPassword || this.merchantResetPassword || this.tourOperatorResetPassword) {
            this.processId = sessionStorage.getItem('processId')+'';
            this.operator.Email = localStorage.getItem('userMail')+'';
        }

        this.clearStorage();
        this.googlePlacesService.addressSuggestions$
            .pipe(takeUntil(this.destroy$))
            .subscribe((response) => {
                this.addressSuggestions = response.suggestions ?? [];

                this.addressSearchError = response.success
                    ? ''
                    : response.errorMessage || 'Address search is unavailable right now. Please type your address manually.';
            });
    }

    ngOnDestroy(): void {
        this.leaveSignupConfirmed$.next(this.allowLeaveSignup);
        this.leaveSignupConfirmed$.complete();
        this.destroy$.next();
        this.destroy$.complete();
        this.clearStorage();
        this.clearGovIdPreviews();
    }

    @HostListener('window:beforeunload', ['$event'])
    onBeforeUnload(event: BeforeUnloadEvent) {
        if (!this.shouldWarnBeforeLeaving()) {
            return;
        }

        event.preventDefault();
    }

    confirmLeaveSignup(): boolean | Observable<boolean> {
        if (!this.shouldWarnBeforeLeaving()) {
            return true;
        }

        this.showLeaveWithoutSavingDialog = true;
        return this.leaveSignupConfirmed$.pipe(take(1));
    }

    stayAndContinueSignup() {
        this.showLeaveWithoutSavingDialog = false;
        this.pendingLeaveAction = null;
        this.leaveSignupConfirmed$.next(false);
    }

    leaveSignupWithoutSaving() {
        this.showLeaveWithoutSavingDialog = false;
        this.allowLeaveSignup = true;
        this.leaveSignupConfirmed$.next(true);

        const leaveAction = this.pendingLeaveAction;
        this.pendingLeaveAction = null;
        leaveAction?.();
    }

    private shouldWarnBeforeLeaving(): boolean {
        if (this.allowLeaveSignup) {
            return false;
        }

        if (this.operatorResetPassword || this.merchantResetPassword || this.tourOperatorResetPassword) {
            return false;
        }

        return true;
    }

    private promptLeaveSignup(onConfirm: () => void) {
        if (!this.shouldWarnBeforeLeaving()) {
            onConfirm();
            return;
        }

        this.pendingLeaveAction = onConfirm;
        this.showLeaveWithoutSavingDialog = true;
    }

    private leaveSignupPage() {
        if (window.location.pathname.startsWith('/operator')) {
            void this.router.navigate(['/operator/login']);
            return;
        }

        this.app.operatorSignUp = false;
        if (this.tourOperatorResetPassword) {
            this.app.tourOperatorLogin = true;
            this.app.activeRoot = RootEnum.PartnerLogin;
        } else if (this.merchantResetPassword) {
            this.app.merchantLogin = true;
            this.app.activeRoot = RootEnum.PartnerLogin;
        } else {
            this.app.operatorLogin = true;
            this.app.activeRoot = RootEnum.OperatorLogin;
        }
    }

    clearStorage() {
        sessionStorage.removeItem('operatorLogin');
        sessionStorage.removeItem('operatorSignUp');
        sessionStorage.removeItem('operatorResetPassword');
        sessionStorage.removeItem('merchantResetPassword');
        sessionStorage.removeItem('tourOperatorResetPassword');
        sessionStorage.removeItem('processId');
    }

    searchPlaces(query: string) {
        const trimmed = (query ?? '').trim();
        if (this.selectedAddress && trimmed === this.selectedAddress) {
            return;
        }

        this.selectedAddress = '';
        this.googlePlacesService.searchPlaces(query);
    }

    async onAddressSelected(address: string) {
        const suggestion = this.addressSuggestions.find((item) => item.text === address);
        this.selectedAddress = (address ?? '').trim();
        this.operator.Address = address;
        this.addressSuggestions = [];
        this.addressSearchError = '';

        // Writing the option into the input already queued a lookup for the full address.
        // Discard it in the same tick, before awaiting Place Details, so a slow fetchFields
        // cannot let the debounce fire another billed request for the address just picked.
        this.googlePlacesService.searchPlaces('');

        await this.googlePlacesService.completeSession(suggestion?.placeId);
    }

    private resetStepToOperatorDetails() {
        this.currentStep = SignupStepEnum.OperatorDetails;
        this.isOtpRequest = false;
        this.formMarginTop = SignupParams.MarginTopPrimary;
        this.step1class = SignupParams.CurrentStep;
        this.step2class = SignupParams.NextStep;
        this.step3class = SignupParams.NextStep;
        this.step4class = SignupParams.NextStep;
        this.btnContinueText = SignupParams.ContinueBtn;
        this.title = SignupParams.FirstTitle;
        this.subtitle = SignupParams.FirstSubtitle;
    }

    alwaysBackToLogin() {
        this.promptLeaveSignup(() => this.leaveSignupPage());
    }

    backToLogin() {
        if (this.currentStep === SignupStepEnum.ReviewSubmit) {
            this.switchStep(SignupStepEnum.AccountCredentials);
            return;
        }

        if (this.currentStep === SignupStepEnum.AccountCredentials) {
            this.switchStep(SignupStepEnum.BusinessRequirements);
            return;
        }

        if (this.currentStep === SignupStepEnum.VerifyEmailOTP || this.currentStep === SignupStepEnum.BusinessRequirements) {
            return this.resetStepToOperatorDetails();
        }

        if (window.location.pathname.startsWith('/operator')) {
            void this.router.navigate(['/operator/login']);
            return;
        }

        this.promptLeaveSignup(() => this.leaveSignupPage());
    }

    isStepPassed(step: string) {
        return step === 'passed-step';
    }

    get signupSteps(): { label: string; state: string; target: SignupStepEnum }[] {
        return [
            { label: `Operator's Details`, state: this.step1class, target: SignupStepEnum.OperatorDetails },
            { label: 'Business Requirements', state: this.step2class, target: SignupStepEnum.BusinessRequirements },
            { label: 'Password Setup', state: this.step3class, target: SignupStepEnum.AccountCredentials },
            { label: 'Review & Submit', state: this.step4class, target: SignupStepEnum.ReviewSubmit }
        ];
    }

    canGoToStep(step: SignupStepEnum) {
        return step < this.currentStep;
    }

    goToStep(step: SignupStepEnum) {
        if (!this.canGoToStep(step)) {
            return;
        }

        this.switchStep(step, true);
    }

    // Maps the shared step states to stepper-local classes so the global .passed-step / .current-step
    // borders used by other components do not leak into the circles and labels here.
    stepStateClass(step: string) {
        switch (step) {
            case SignupParams.PassedStep:
                return 'signup-step-passed';
            case SignupParams.CurrentStep:
                return 'signup-step-current';
            default:
                return 'signup-step-upcoming';
        }
    }

    stepValidation() {
        switch (this.currentStep) {
            case SignupStepEnum.OperatorDetails:
                if (!this.operator.IsTermsAccepted) {
                    this.termsErrorVisible = true;
                }
                if (!this.validateEmail() || !this.operator.IsTermsAccepted
                    || Utils.isNullOrEmpty(this.operator.CompanyName)
                    || Utils.isNullOrEmpty(this.operator.FirstName)
                    || Utils.isNullOrEmpty(this.operator.LastName)) {
                    return false;
                } else {
                    this.termsErrorVisible = false;
                    this.emailErrorMsg = null;
                }
                break;
            case SignupStepEnum.BusinessRequirements:
                return true;
            case SignupStepEnum.AccountCredentials:
                if (!this.operator.Password || !this.operator.ConfirmPassword || (this.operator.Password !== this.operator.ConfirmPassword)) {
                    return false;
                }
                break;
            case SignupStepEnum.ReviewSubmit:
                return true;
            case SignupStepEnum.VerifyEmailOTP:
                return this.isOtpVerified;
            default: return false;
        }

        return true;
    }

    switchStep(step: SignupStepEnum, fromBreadCrumbs: boolean = false) {
        const goingBack = step < this.currentStep;
        if ((fromBreadCrumbs && step > this.currentStep) || (!goingBack && !this.stepValidation())) {
            return;
        }

        this.currentStep = step;
        switch (this.currentStep) {
            case SignupStepEnum.OperatorDetails:
                this.formMarginTop = SignupParams.MarginTopPrimary;
                this.step1class = SignupParams.CurrentStep;
                this.step2class = SignupParams.NextStep;
                this.step3class = SignupParams.NextStep;
                this.step4class = SignupParams.NextStep;
                this.btnContinueText = SignupParams.NextBtn;
                this.title = SignupParams.FirstTitle;
                this.subtitle = SignupParams.FirstSubtitle;
                this.isOtpRequest = false;
                break;
            case SignupStepEnum.AccountCredentials:
                this.formMarginTop = SignupParams.MarginTopSecondary;
                this.step1class = SignupParams.PassedStep;
                this.step2class = SignupParams.PassedStep;
                this.step3class = SignupParams.CurrentStep;
                this.step4class = SignupParams.NextStep;
                this.btnContinueText = SignupParams.ContinueBtn;
                this.title = SignupParams.SecondTitle;
                this.subtitle = SignupParams.SecondSubtitle;
                break;
            case SignupStepEnum.ReviewSubmit:
                this.formMarginTop = SignupParams.MarginTopPrimary;
                this.step1class = SignupParams.PassedStep;
                this.step2class = SignupParams.PassedStep;
                this.step3class = SignupParams.PassedStep;
                this.step4class = SignupParams.CurrentStep;
                this.btnContinueText = SignupParams.SignupSubmitBtn;
                this.title = SignupParams.ReviewTitle;
                this.subtitle = SignupParams.ReviewSubtitle;
                this.isOtpRequest = false;
                this.showPasswordTips = false;
                break;
            case SignupStepEnum.BusinessRequirements:
                this.formMarginTop = SignupParams.MarginTopPrimary;
                this.step1class = SignupParams.PassedStep;
                this.step2class = SignupParams.CurrentStep;
                this.step3class = SignupParams.NextStep;
                this.step4class = SignupParams.NextStep;
                this.btnContinueText = SignupParams.ContinueBtn;
                this.title = SignupParams.ThirdTitle;
                this.isOtpRequest = false;
                this.subtitle = SignupParams.ThirdSubtitle;
                break;
            case SignupStepEnum.VerifyEmailOTP:
                this.title = '';
                this.subtitle = '';
                this.isOtpRequest = true;
                break;
        }
    }

    deleteDocuments(links: string[]) {
        this.operatorService.deleteDocuments(links, this.messageService)
            .then(data => {
            });

    }

    uploadDocuments(files: File[], docType: BusinessDocumentsEnum) {
        switch (docType) {
            case BusinessDocumentsEnum.GovernmentIssuedId:
                if (!files || !files.length) {
                    this.govIdUploadError = true;
                    return;
                }
                clearInterval(this.govIdInterval);
                this.govIdProgressBar = 0;
                this.operator.GovIdData = [...this.operator.GovIdData, ...files];
                files.forEach(file => this.getGovIdPreviewSrc(file));
                this.govIdUploadError = false;
                this.govIdInterval = setInterval(() => {
                    if (this.govIdProgressBar === 100) {
                        clearInterval(this.govIdInterval);
                    } else {
                        this.govIdProgressBar += 1;
                    }
                }, 100);
                break;
            case BusinessDocumentsEnum.DTIorSECCertificate:
                if (!files || !files.length) {
                    this.DTIorSECUploadError = true;
                    return;
                }
                this.operator.DTIorSECCertifData = files;
                this.DTIorSECUploadError = false;
                this.DTIorSECInterval = setInterval(() => {
                    if (this.DTIorSECProgressBar === 100) {
                        clearInterval(this.DTIorSECInterval);
                    } else {
                        this.DTIorSECProgressBar += 5;
                    }
                }, 100);
                break;
            case BusinessDocumentsEnum.BIRCertificate:
                if (!files || !files.length) {
                    this.BIRUploadError = true;
                    return;
                }
                this.operator.BIRCertifData = files;
                this.BIRUploadError = false;
                this.BIRInterval = setInterval(() => {
                    if (this.BIRProgressBar === 100) {
                        clearInterval(this.BIRInterval);
                    } else {
                        this.BIRProgressBar += 5;
                    }
                }, 100);
                break;
            case BusinessDocumentsEnum.LTFRBCertificate:
                if (!files || !files.length) {
                    this.LTFRBUploadError = true;
                    return;
                }
                this.operator.LTFRBCertifData = files;
                this.LTFRBUploadError = false;
                this.LTFRBInterval = setInterval(() => {
                    if (this.LTFRBProgressBar === 100) {
                        clearInterval(this.LTFRBInterval);
                    } else {
                        this.LTFRBProgressBar += 5;
                    }
                }, 100);
                break;
            case BusinessDocumentsEnum.BusinessPermit:
                if (!files || !files.length) {
                    this.BusinessPermitUploadError = true;
                    return;
                }
                this.operator.BusinessPermitData = files;
                this.BusinessPermitUploadError = false;
                this.BusinessPermitInterval = setInterval(() => {
                    if (this.BusinessPermitProgressBar === 100) {
                        clearInterval(this.BusinessPermitInterval);
                    } else {
                        this.BusinessPermitProgressBar += 5;
                    }
                }, 100);
                break;
        }

        const formData = new FormData();
        for (const file of files) {
            formData.append('formFiles', file);
        }

        this.operatorService.uploadDocuments(formData, this.messageService)
            .then(data => {
                if (!data || data.Success === false) {
                    this.afterUploadError(docType, files);
                    this.messageService.add({
                        severity: 'error',
                        summary: 'Upload failed',
                        detail: data?.ErrorMessage || 'File was not accepted. Use .pdf, .jpg, .jpeg, or .png (max 10MB).'
                    });
                    return;
                }
                const urls = normalizeUploadedUrls(data.UploadedDocsUrls);
                if (!urls.length) {
                    this.afterUploadError(docType, files);
                    this.messageService.add({
                        severity: 'error',
                        summary: 'Upload failed',
                        detail: 'File was not accepted. Use .pdf, .jpg, .jpeg, or .png (max 10MB).'
                    });
                    return;
                }
                this.afterUploadSuccess(docType, urls);
            })
            .catch(err => {
                this.afterUploadError(docType, files);
                throw err;
            });
    }

    afterUploadSuccess(docType: BusinessDocumentsEnum, docUrls: string[]) {
        switch (docType) {
            case BusinessDocumentsEnum.GovernmentIssuedId:
                this.operator.GovIdUrls = [...this.operator.GovIdUrls, ...docUrls];
                this.govIdProgressBar = 100;
                break;
            case BusinessDocumentsEnum.DTIorSECCertificate:
                this.operator.DTIorSECCertifUrls = docUrls;
                this.DTIorSECProgressBar = 100;
                break;
            case BusinessDocumentsEnum.BIRCertificate:
                this.operator.BIRCertifUrls = docUrls;
                this.BIRProgressBar = 100;
                break;
            case BusinessDocumentsEnum.LTFRBCertificate:
                this.operator.LTFRBCertifUrls = docUrls;
                this.LTFRBProgressBar = 100;
                break;
            case BusinessDocumentsEnum.BusinessPermit:
                this.operator.BusinessPermitUrls = docUrls;
                this.BusinessPermitProgressBar = 100;
                break;
        }
    }

    afterUploadError(docType: BusinessDocumentsEnum, pendingFiles: File[] = []) {
        switch (docType) {
            case BusinessDocumentsEnum.GovernmentIssuedId:
                clearInterval(this.govIdInterval);
                if (pendingFiles.length) {
                    pendingFiles.forEach(file => this.clearGovIdPreview(file));
                    this.operator.GovIdData = this.operator.GovIdData.filter(file => !pendingFiles.includes(file));
                }
                this.govIdProgressBar = this.operator.GovIdData.length ? 100 : 0;
                this.govIdUploadError = true;
                break;
            case BusinessDocumentsEnum.DTIorSECCertificate:
                clearInterval(this.DTIorSECInterval);
                this.DTIorSECProgressBar = 0;
                this.DTIorSECUploadError = true;
                break;
            case BusinessDocumentsEnum.BIRCertificate:
                clearInterval(this.BIRInterval);
                this.BIRProgressBar = 0;
                this.BIRUploadError = true;
                break;
            case BusinessDocumentsEnum.LTFRBCertificate:
                clearInterval(this.LTFRBInterval);
                this.LTFRBProgressBar = 0;
                this.LTFRBUploadError = true;
                break;
            case BusinessDocumentsEnum.BusinessPermit:
                clearInterval(this.BusinessPermitInterval);
                this.BusinessPermitProgressBar = 0;
                this.BusinessPermitUploadError = true;
                break;
        }
    }

    openPasswordTips() {
        this.showPasswordTips = true;
    }

    closePasswordTips() {
        this.showPasswordTips = false;
    }

    continue() {
        switch (this.currentStep) {
            case SignupStepEnum.OperatorDetails:
                this.preSignUp();
                break;
            case SignupStepEnum.AccountCredentials:
                this.switchStep(SignupStepEnum.ReviewSubmit);
                break;
            case SignupStepEnum.ReviewSubmit:
                this.signUp();
                break;
            case SignupStepEnum.BusinessRequirements:
                this.switchStep(SignupStepEnum.AccountCredentials);
                break;
            case SignupStepEnum.VerifyEmailOTP:
                this.switchStep(SignupStepEnum.VerifyEmailOTP);
                break;
        }
    }

    editReviewSection(step: SignupStepEnum) {
        this.switchStep(step);
    }

    updateAccountDetails() {
        this.showLoadingOverlay();
        this.operatorService.updateAccount(this.operator, this.messageService)
            .then(data => {
                this.hideOverlayWithDelay(5000);
            })
            .catch(err => {
                this.hideOverlayWithDelay(5000);
                throw err;
            });
    }

    preSignUp(){
        if (!this.operator) {
            console.error('Operator data is empty');
            return;
        }

        this.showVerifyingEmailDialog();
        this.operatorService.preCreateOperator(this.operator, this.messageService)
            .then(data => {
                if (!data.Success) {
                    this.toggleAccountCreationError(true);
                    this.hideVerifyingEmailDialog();
                    this.messageService.add({ severity: 'error', summary: 'Error', detail: data.ErrorMessage });
                    return;
                }
                this.hideVerifyingEmailDialog();
                this.signUpProcessId = data.ProcessId;
                this.switchStep(SignupStepEnum.VerifyEmailOTP);
            })
            .catch(err => {
                this.toggleAccountCreationError(true);
                this.hideVerifyingEmailDialog();
                this.messageService.add({ severity: 'error', summary: 'Error', detail: err });
            });
    }

    signUp() {
        if (!this.operator) {
            console.error('Operator data is empty');
            return;
        }

        this.toggleAccountCreationError(false);
        this.showAccountCreationDialog();
        this.operatorService.signUp(this.operator, this.messageService)
            .then(data => {
                if (!data.Success) {
                    this.toggleAccountCreationError(true);
                    this.hideAccountCreationDialog();
                    this.messageService.add({ severity: 'error', summary: 'Error', detail: data.ErrorMessage });
                    return;
                }
                this.hideAccountCreationDialog();
                this.signUpProcessId = data.ProcessId;
                this.login();
            })
            .catch(err => {
                this.toggleAccountCreationError(true);
                this.hideAccountCreationDialog();
                throw err;
            });
    }

    getRoleId() {
        if (this.merchantResetPassword) {
            return XpressUserRoleEnum.Merchant;
        }
        else if (this.tourOperatorResetPassword) {
            return XpressUserRoleEnum.TourOperator;
        }

        return XpressUserRoleEnum.Operator;
    }

    //Call api /Login to get ProcessId
    login() {
        window.sessionStorage.removeItem('operatorLogin');
        window.sessionStorage.removeItem('operatorSignUp');
        window.sessionStorage.removeItem('operatorResetPassword');
        window.sessionStorage.removeItem('merchantResetPassword');
        window.sessionStorage.removeItem('tourOperatorResetPassword');
        this.allowLeaveSignup = true;

        this.apiUrl = this.apiBaseUrl+'' + this.apiVersion;
        const userName = this.operator.Email;
        const password = this.operator.Password;
        const role = this.getRoleId();
        const data = 'username=' + encodeURIComponent(userName) + '&password=' + encodeURIComponent(password) + '&roleId=' + role;

        if (this.operatorResetPassword || this.merchantResetPassword || this.tourOperatorResetPassword) {
            this.passChangedSuccess = false;
            this.showLoadingOverlay();
        }
        this.showLoadingOverlay();
        this.authService.login(this.apiUrl, data)
            .then(response => response.json())
            .then(tmp => {
                if ((!tmp.Success && !tmp.ProcessId) || tmp.error) {
                    this.hideAccountCreationDialog();
                    this.hideLoadingOverlay();
                    if (this.accErrorMsg !== undefined) {
                        this.accErrorMsg = 'Login failed';
                    }
                    return;
                }
                this.offerDashboardTourOnFirstLogin = !!tmp.IsFirstLogin
                    && !this.operatorResetPassword
                    && !this.merchantResetPassword
                    && !this.tourOperatorResetPassword;
                const processId = tmp.ProcessId;
                this.checkOneTimePassword(processId);
            });
    }

    //Call api CheckOneTimePassword for mark OTP as verified
    checkOneTimePassword(processId: string) {
        const checkOtpApiUrl = this.apiUrl + 'api/Otp/CheckOTP';
        this.authService.checkOneTimePassword(checkOtpApiUrl, processId)
            .then(response => response.json())
            .then(checkOtpResponse => {
                if ((!checkOtpResponse.Success && !checkOtpResponse.ProcessId) || checkOtpResponse.error) {
                    this.hideAccountCreationDialog();
                    if (this.accErrorMsg !== undefined) {
                        this.accErrorMsg = 'One-time password check failed';
                    }
                    return;
                }
                this.token(processId);
            });
    }

    //Call api token with ProcessId for get auth data
    token(processId: string) {
        const tokenApiUrl = this.apiUrl + 'token';
        this.authService.token(tokenApiUrl, processId)
            .then(response => response.json())
            .then(data => {
                if (!data.access_token || data.error) {
                    if (this.accErrorMsg !== undefined) {
                        this.accErrorMsg = 'Failed to get token';
                    }
                    this.hideAccountCreationDialog();
                    return;
                }
                const authData = {
                    access_token: data.access_token,
                    '.expires': data['.expires']
                };

                const authDataJson = JSON.stringify(authData);
                localStorage.setItem('xpressAuth', authDataJson);
                sessionStorage.setItem('isSignup', 'true');
                if (this.offerDashboardTourOnFirstLogin) {
                    OperatorDashboardTourService.markFirstLogin();
                }
                this.app.processAuthentication(authDataJson);
                if (!this.operatorResetPassword && !this.merchantResetPassword && !this.tourOperatorResetPassword) {
                    this.updateAccountDetails();
                    this.goToOperatorsMenu();
                } else {
                    setTimeout(() => {
                        if (this.operatorResetPassword) {
                            this.goToOperatorsMenu();
                        } else if (this.merchantResetPassword || this.tourOperatorResetPassword) {
                            sessionStorage.removeItem('isSignup');
                            this.app.activeRoot = RootEnum.PartnerDashboardSwitch;
                            this.app.operatorSignUp = false;
                        }
                        this.hideLoadingOverlay();
                    }, 3000);
                }
                this.hideAccountCreationDialog();

                // this.app.activeRoot = RootEnum.OpsTower;
                // this.app.operatorSignUp = false;
            });
    }

    goToOperatorsMenu() {
        sessionStorage.removeItem('isSignup');

        if(this.appData.hasPermission(MenuPermissionConfig.OPERATOR_DASHBOARD, MenuPermissionActionEnum.Read)) {
            void this.router.navigate(['/operator/dashboard/home']);
        } else if(this.appData.hasPermission(MenuPermissionConfig.OPERATOR_REPORTS, MenuPermissionActionEnum.Read)) {
            void this.router.navigate(['/operator/dashboard/reports']);
        }

        this.app.operatorSignUp = false;
    }

    toggleAccountCreationError(raise: boolean) {
        if (raise) {
            this.accErrorMsg = 'An error occurred while creating an account';
        } else {
            this.accErrorMsg = null;
        }
    }

    doItLater() {
        this.switchStep(SignupStepEnum.AccountCredentials);
    }

    isGovIdUploaded() {
        return this.operator.GovIdData.length > 0
            && this.govIdProgressBar === 100;
    }

    get passLevel(): XpPasswordStrengthLevel {
        return xpPasswordStrength(this.operator.Password, SignupParams.PassMinLength);
    }

    canContinue() {
        switch (this.currentStep) {
            case SignupStepEnum.OperatorDetails:
                return (this.isEmailFormatValid(this.operator.Email) && !this.emailAlreadyExist
                    && this.operator.IsTermsAccepted
                    && !Utils.isNullOrEmpty(this.operator.CompanyName)
                    && !Utils.isNullOrEmpty(this.operator.FirstName)
                    && !Utils.isNullOrEmpty(this.operator.LastName));
            case SignupStepEnum.AccountCredentials:
                return (!Utils.isNullOrEmpty(this.operator.Password) && !Utils.isNullOrEmpty(this.operator.ConfirmPassword)
                    && (this.operator.Password === this.operator.ConfirmPassword) && this.passLevel === 'strong');
            case SignupStepEnum.BusinessRequirements:
                return this.isGovIdUploaded();
            case SignupStepEnum.VerifyEmailOTP:
                return true; // for default
            case SignupStepEnum.ReviewSubmit:
                return this.passLevel === 'strong'
                    && (this.operator.Password === this.operator.ConfirmPassword)
                    && !Utils.isNullOrEmpty(this.operator.CompanyName);
        }
    }

    canSubmit() {
        return (!Utils.isNullOrEmpty(this.operator.Password) && !Utils.isNullOrEmpty(this.operator.ConfirmPassword)
            && (this.operator.Password === this.operator.ConfirmPassword) && this.passLevel === 'strong');
    }

    changePassword() {
        if (!(!Utils.isNullOrEmpty(this.operator.Password) && !Utils.isNullOrEmpty(this.operator.ConfirmPassword)
            && (this.operator.Password === this.operator.ConfirmPassword) && this.passLevel === 'strong')) {
            return;
        }
        const dto = {
            ProcessId: this.processId,
            OldPassword: this.operator.Password,
            NewPassword: this.operator.Password,
            ConfirmPassword: this.operator.ConfirmPassword,
            RoleId: this.getRoleId()
        };

        this.xpressUserService.resetPassword(dto, this.messageService)
            .then(data => {
                if (!data.Success) {
                    this.messageService.add({ severity: 'error', summary: 'Error', detail: data.ErrorMessage });
                    return;
                }

                this.passChangedSuccess = true;
            });

    }

    onEmailChange() {
        this.emailAlreadyExist = false;
        this.emailAvailabilityChecked = false;
        this.emailErrorMsg = null;
    }

    isEmailAvailable() {
        return this.emailAvailabilityChecked && !this.emailAlreadyExist;
    }

    private isEmailFormatValid(email: string | null | undefined): boolean {
        const value = (email ?? '').trim();
        return !!value && Utils.emailRegex.test(value);
    }

    validateEmail() {
        const email = (this.operator.Email ?? '').trim();
        if (!email) {
            this.emailErrorMsg = 'Field is required';
            return false;
        }
        if (!this.isEmailFormatValid(email)) {
            this.emailErrorMsg = 'This email is not valid';
            return false;
        }
        if (this.emailAlreadyExist) {
            this.emailErrorMsg = 'This email is already taken';
            return false;
        }

        this.emailErrorMsg = null;
        return true;
    }

    validateTaxNumber() {
        this.taxErrorVisible = !this.operator.TaxNumber;
    }

    checkEmailExist() {
        const isValidEmail = this.validateEmail();

        if (!isValidEmail) {
            return;
        }

        if (!this.operator.Email) {
            return;
        }

        this.loading = true;
        const dto = {
            Email: this.operator.Email
        };
        this.operatorService.checkEmailExist(dto, this.messageService)
            .then(data => {
                this.emailAlreadyExist = data.Found;
                this.emailAvailabilityChecked = true;
                this.validateEmail();
                this.loading = false;
            })
            .catch(err => {
                this.loading = false;
                throw err;
            });
    }

    onFileDropped(docType: BusinessDocumentsEnum, $event: any) {
        this.prepareFiles(docType, $event);
    }

    fileBrowseHandler(docType: BusinessDocumentsEnum, files: any) {
        this.prepareFiles(docType, files);
    }

    prepareFiles(docType: BusinessDocumentsEnum, files: any) {
        let filesArr: any[];
        filesArr = files?.target?.files ? Array.from(files.target.files) : Array.from(files);
        if (files?.target) {
            files.target.value = '';
        }

        const selectedCount = filesArr.length;
        const maxCount = docType === BusinessDocumentsEnum.GovernmentIssuedId
            ? 2
            : SignupParams.MAX_UPLOAD_FILES_COUNT;
        const remainingSlots = Math.max(0, maxCount - this.getUploadedFilesCount(docType));
        filesArr = filesArr.filter((uploadedFile: File) => SignupParams.ACCEPTED_FILE_TYPES.includes(uploadedFile.type));
        filesArr = filesArr.filter((uploadedFile: File) => uploadedFile.size <= SignupParams.MAX_UPLOAD_FILE_SIZE);
        filesArr = filesArr.slice(0, remainingSlots);

        if (selectedCount > 0 && !filesArr.length) {
            this.setUploadError(docType, true);
            return;
        }

        if (!filesArr.length) {
            return;
        }

        this.setUploadError(docType, false);
        this.uploadDocuments(filesArr, docType);
    }

    private getUploadedFilesCount(docType: BusinessDocumentsEnum): number {
        switch (docType) {
            case BusinessDocumentsEnum.GovernmentIssuedId:
                return this.operator.GovIdData.length;
            case BusinessDocumentsEnum.DTIorSECCertificate:
                return this.operator.DTIorSECCertifData.length;
            case BusinessDocumentsEnum.BIRCertificate:
                return this.operator.BIRCertifData.length;
            case BusinessDocumentsEnum.LTFRBCertificate:
                return this.operator.LTFRBCertifData.length;
            case BusinessDocumentsEnum.BusinessPermit:
                return this.operator.BusinessPermitData.length;
            default:
                return 0;
        }
    }

    private setUploadError(docType: BusinessDocumentsEnum, hasError: boolean) {
        switch (docType) {
            case BusinessDocumentsEnum.GovernmentIssuedId:
                this.govIdUploadError = hasError;
                break;
            case BusinessDocumentsEnum.DTIorSECCertificate:
                this.DTIorSECUploadError = hasError;
                break;
            case BusinessDocumentsEnum.BIRCertificate:
                this.BIRUploadError = hasError;
                break;
            case BusinessDocumentsEnum.LTFRBCertificate:
                this.LTFRBUploadError = hasError;
                break;
            case BusinessDocumentsEnum.BusinessPermit:
                this.BusinessPermitUploadError = hasError;
                break;
        }
    }

    deleteUpload(docType: BusinessDocumentsEnum, file: File) {
        let link = '';
        let fileIndex = -1;
        switch (docType) {
            case BusinessDocumentsEnum.GovernmentIssuedId:
                fileIndex = this.operator.GovIdData.indexOf(file);
                if (fileIndex > -1) {
                    link = this.operator.GovIdUrls[fileIndex];
                    this.clearGovIdPreview(file);
                    this.operator.GovIdData.splice(fileIndex, 1);
                    this.operator.GovIdUrls.splice(fileIndex, 1);
                }
                if (!this.operator.GovIdData.length) {
                    this.govIdProgressBar = 0;
                }
                break;
            case BusinessDocumentsEnum.DTIorSECCertificate:
                fileIndex = this.operator.DTIorSECCertifData.indexOf(file);
                link = this.operator.DTIorSECCertifUrls[fileIndex];
                this.operator.DTIorSECCertifData.splice(fileIndex, 1);
                if (!this.operator.DTIorSECCertifData.length) {
                    this.DTIorSECProgressBar = 0;
                }
                break;
            case BusinessDocumentsEnum.BIRCertificate:
                fileIndex = this.operator.BIRCertifData.indexOf(file);
                link = this.operator.BIRCertifUrls[fileIndex];
                this.operator.BIRCertifData.splice(fileIndex, 1);
                if (!this.operator.BIRCertifData.length) {
                    this.BIRProgressBar = 0;
                }
                break;
            case BusinessDocumentsEnum.LTFRBCertificate:
                fileIndex = this.operator.LTFRBCertifData.indexOf(file);
                link = this.operator.LTFRBCertifUrls[fileIndex];
                this.operator.LTFRBCertifData.splice(fileIndex, 1);
                if (!this.operator.LTFRBCertifData.length) {
                    this.LTFRBProgressBar = 0;
                }
                break;
            case BusinessDocumentsEnum.BusinessPermit:
                fileIndex = this.operator.BusinessPermitData.indexOf(file);
                link = this.operator.BusinessPermitUrls[fileIndex];
                this.operator.BusinessPermitData.splice(fileIndex, 1);
                if (!this.operator.BusinessPermitData.length) {
                    this.BusinessPermitProgressBar = 0;
                }
                break;
        }

        this.deleteDocuments([link]);
    }

    cancelUpload(docType: BusinessDocumentsEnum) {
        switch (docType) {
            case BusinessDocumentsEnum.GovernmentIssuedId:
                clearInterval(this.govIdInterval);
                this.govIdProgressBar = 0;
                this.clearGovIdPreviews();
                this.operator.GovIdData = [];
                break;
            case BusinessDocumentsEnum.DTIorSECCertificate:
                clearInterval(this.DTIorSECInterval);
                this.DTIorSECProgressBar = 0;
                this.operator.DTIorSECCertifData = [];
                break;
            case BusinessDocumentsEnum.BIRCertificate:
                clearInterval(this.BIRInterval);
                this.BIRProgressBar = 0;
                this.operator.BIRCertifData = [];
                break;
            case BusinessDocumentsEnum.LTFRBCertificate:
                clearInterval(this.LTFRBInterval);
                this.LTFRBProgressBar = 0;
                this.operator.LTFRBCertifData = [];
                break;
            case BusinessDocumentsEnum.BusinessPermit:
                clearInterval(this.BusinessPermitInterval);
                this.BusinessPermitProgressBar = 0;
                this.operator.BusinessPermitData = [];
                break;
        }
    }

    showLoadingOverlay() {
        this.isLoadingOverlay = true;
    }

    hideOverlayWithDelay(delayInMilliSec: number) {
        setTimeout(() => {
            this.hideLoadingOverlay();
        }, delayInMilliSec);
    }

    hideLoadingOverlay() {
        this.isLoadingOverlay = false;
    }

    showAccountCreationDialog() {
        this.accountCreationInProgress = true;
    }

    hideAccountCreationDialog() {
        this.accountCreationInProgress = false;
    }

    showVerifyingEmailDialog() {
        this.verifyingEmailInProgress = true;
    }

    hideVerifyingEmailDialog() {
        this.verifyingEmailInProgress = false;
    }

    checkIsPasswordEqual() {
        this.isPasswordEqual = (this.operator.Password ?? '') === (this.operator.ConfirmPassword ?? '');
    }

    onOtpVerified(processId: string){
        this.isOtpRequest = false;
        this.isOtpVerified = true;
        this.switchStep(SignupStepEnum.BusinessRequirements);
    }

    deleteSelectedFile(){

    }

    formatFileSizeMb(bytes: number): string {
        const mb = bytes / (1024 * 1024);
        if (!Number.isFinite(mb) || mb === 0) {
            return '0';
        }

        const [integerPart, fractionPart = ''] = mb.toFixed(20).replace(/\.?0+$/, '').split('.');
        if (!fractionPart) {
            return integerPart;
        }

        const firstNonZeroIndex = fractionPart.search(/[1-9]/);
        if (firstNonZeroIndex === -1) {
            return integerPart;
        }

        return `${integerPart}.${fractionPart.slice(0, firstNonZeroIndex + 1)}`;
    }

    getGovIdPreviewSrc(file: File): string {
        const cached = this.govIdPreviewUrls.get(file);
        if (cached) {
            return cached;
        }

        // The icon shows while the image is being read, and stays for PDFs and unreadable files.
        const fileIcon = Utils.getUploadedFileIcon(file?.type);
        this.govIdPreviewUrls.set(file, fileIcon);

        if (file && file.type !== 'application/pdf') {
            const reader = new FileReader();
            reader.onload = () => {
                const dataUrl = reader.result;
                if (typeof dataUrl === 'string' && this.govIdPreviewUrls.has(file)) {
                    this.govIdPreviewUrls.set(file, dataUrl);
                }
            };
            reader.readAsDataURL(file);
        }

        return fileIcon;
    }

    private clearGovIdPreview(file: File) {
        this.govIdPreviewUrls.delete(file);
    }

    private clearGovIdPreviews() {
        this.govIdPreviewUrls.clear();
    }

    protected readonly SignupStepEnum = SignupStepEnum;
    protected readonly BusinessDocumentsEnum = BusinessDocumentsEnum;
    protected readonly SignupParams = SignupParams;
    protected readonly Utils = Utils;
}
